import { useEffect, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ErrorBoundary } from "@/components/error-boundary";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import { Route, Switch, useLocation, Router as WouterRouter } from "wouter";
import {
  DashboardProvider,
  useDashboard,
} from "@/features/dashboard/dashboard-context";
import { I18nProvider } from "@/i18n/i18n-context";
import { AppShell } from "@/components/app-shell";
import Overview from "@/pages/overview";
import Wallets from "@/pages/wallets";
import Transactions from "@/pages/transactions";
import Verification from "@/pages/verification";
import Settings from "@/pages/settings";
import LockedModule from "@/pages/locked-module";
import ApiReferencePage from "@/features/developers/api-reference-page";
import { LoginPage } from "@/features/auth/login-page";
import { RegisterPage } from "@/features/auth/register-page";
import { AuthProvider, useAuth } from "@/features/auth/auth-context";
import AccountsPage from "@/features/onboarding/accounts-page";
import CardsPage from "@/features/cards/cards-page";
import AuthActionsPage from "@/features/auth/auth-actions-page";
import TeamPage from "@/features/team/team-page";
import ApplicationsPage from "@/features/developers/applications-page";
import PaymentLinksPage from "@/features/payment-links/payment-links-page";
import CheckoutPage from "@/features/payment-links/checkout-page";
import NotificationsPage from "@/features/notifications/notifications-page";
import FinancePage from "@/features/finance/finance-page";
import AvailableMarkets from "@/pages/available-markets";
import MerchantAssistantPage from "@/features/assistant/merchant-assistant-page";
import SupportTicketsPage from "@/features/assistant/support-tickets-page";
import {
  PublicStatusDetailPage,
  PublicStatusHistoryPage,
  PublicStatusPage,
  PublicStatusUptimePage,
} from "@/pages/public-status";

function BusinessGate({
  module,
  children,
}: {
  module: "equipe" | "developpeurs";
  children: ReactNode;
}) {
  const { isBusiness, locked, loading } = useDashboard();
  if (loading) {
    return (
      <div
        className="flex min-h-[40vh] items-center justify-center p-8"
        role="status"
      >
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="sr-only">Chargement…</span>
      </div>
    );
  }
  if (!isBusiness || locked) return <LockedModule module={module} />;
  return <>{children}</>;
}

const queryClient = new QueryClient();

function VerificationGate({ children }: { children: ReactNode }) {
  const { loading, verificationStatus } = useDashboard();
  const [location, navigate] = useLocation();
  const verificationRoute = location === "/verification";

  useEffect(() => {
    if (!loading && verificationStatus !== "APPROVED" && !verificationRoute) {
      navigate("/verification", { replace: true });
    }
  }, [loading, navigate, verificationRoute, verificationStatus]);

  if (loading) {
    return (
      <div
        className="flex min-h-[100dvh] items-center justify-center bg-background"
        role="status"
      >
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <span className="sr-only">Chargement…</span>
      </div>
    );
  }
  if (verificationStatus !== "APPROVED" && !verificationRoute) {
    return (
      <div
        className="flex min-h-[100dvh] items-center justify-center bg-background"
        role="status"
      >
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <span className="sr-only">Redirection…</span>
      </div>
    );
  }
  return <>{children}</>;
}

function Router() {
  const { identity, initializing } = useAuth();
  const [location] = useLocation();

  if (window.location.hostname === "status.bizconnectpay.com") {
    if (location === "/history") return <PublicStatusHistoryPage />;
    if (location === "/uptime") return <PublicStatusUptimePage />;
    const detailMatch = location.match(/^\/(?:incidents|events)\/([^/]+)\/?$/);
    if (detailMatch) return <PublicStatusDetailPage id={detailMatch[1]} />;
    if (location === "/" || location === "") return <PublicStatusPage />;
    return <NotFound />;
  }

  if (
    /^\/payment-links\/[^/]+\/checkout\/?$/.test(location) ||
    /^\/link\/[^/]+\/?$/.test(location)
  ) {
    return <CheckoutPage />;
  }
  if (
    [
      "/verify-email",
      "/verifier-email",
      "/forgot-password",
      "/mot-de-passe-oublie",
      "/reset-password",
      "/reinitialiser-mot-de-passe",
    ].includes(location)
  ) {
    const mode =
      location.includes("verify") || location.includes("verifier")
        ? "verify-email"
        : location.includes("reset") || location.includes("reinitialiser")
          ? "reset-password"
          : "forgot-password";
    return <AuthActionsPage mode={mode} />;
  }

  if (initializing) {
    return (
      <div
        className="flex min-h-[100dvh] items-center justify-center bg-background"
        role="status"
      >
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <span className="sr-only">Chargement de votre espace sécurisé…</span>
      </div>
    );
  }

  if (!identity) {
    return (
      <RoutedErrorBoundary>
        <Switch>
          <Route path="/register" component={RegisterPage} />
          <Route path="/inscription" component={RegisterPage} />
          <Route component={LoginPage} />
        </Switch>
      </RoutedErrorBoundary>
    );
  }

  return (
    <DashboardProvider key={`${identity.userId}:${identity.accountId}`}>
      <RoutedErrorBoundary>
        <Switch>
          <Route path="/login">{() => <RedirectToDashboard />}</Route>
          <Route path="/register">{() => <RedirectToDashboard />}</Route>
          <Route>
            {() => (
              <VerificationGate>
                <AppShell>
                  <Switch>
                    <Route path="/" component={Overview} />
                    <Route path="/wallets" component={Wallets} />
                    <Route path="/transactions" component={Transactions} />
                    <Route path="/verification" component={Verification} />
                    <Route path="/settings" component={Settings} />
                    <Route path="/accounts" component={AccountsPage} />
                    <Route path="/comptes" component={AccountsPage} />
                    <Route path="/cards" component={CardsPage} />
                    <Route path="/cartes" component={CardsPage} />
                    <Route path="/finance" component={FinancePage} />
                    <Route path="/services" component={AvailableMarkets} />
                    <Route
                      path="/notifications"
                      component={NotificationsPage}
                    />
                    <Route path="/assistant">
                      {() => (
                        <BusinessGate module="developpeurs">
                          <MerchantAssistantPage />
                        </BusinessGate>
                      )}
                    </Route>
                    <Route path="/support" component={SupportTicketsPage} />
                    <Route path="/applications">
                      {() => (
                        <BusinessGate module="developpeurs">
                          <ApplicationsPage />
                        </BusinessGate>
                      )}
                    </Route>
                    <Route path="/developpeurs">
                      {() => (
                        <BusinessGate module="developpeurs">
                          <ApplicationsPage />
                        </BusinessGate>
                      )}
                    </Route>
                    <Route path="/documentation">
                      {() => (
                        <BusinessGate module="developpeurs">
                          <ApiReferencePage />
                        </BusinessGate>
                      )}
                    </Route>
                    <Route path="/api">
                      {() => (
                        <BusinessGate module="developpeurs">
                          <ApiReferencePage />
                        </BusinessGate>
                      )}
                    </Route>
                    <Route path="/equipe">
                      {() => (
                        <BusinessGate module="equipe">
                          <TeamPage />
                        </BusinessGate>
                      )}
                    </Route>
                    <Route
                      path="/liens-de-paiement"
                      component={PaymentLinksPage}
                    />
                    <Route component={NotFound} />
                  </Switch>
                </AppShell>
              </VerificationGate>
            )}
          </Route>
        </Switch>
      </RoutedErrorBoundary>
    </DashboardProvider>
  );
}

function RedirectToDashboard() {
  const [, navigate] = useLocation();
  useEffect(() => {
    navigate("/", { replace: true });
  }, [navigate]);
  return null;
}

function RoutedErrorBoundary({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  return <ErrorBoundary resetKey={location}>{children}</ErrorBoundary>;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <TooltipProvider>
          <WouterRouter
            base={(import.meta.env.BASE_URL ?? "/").replace(/\/$/, "")}
          >
            <AuthProvider>
              <Router />
            </AuthProvider>
          </WouterRouter>
          <Toaster />
        </TooltipProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
}

export default App;
